/**
 * ModuleBulkActionBar — floating bulk-action bar for a module selection.
 *
 * Same shape as CalibreBulkActionBar and WinderBulkActionBar (dark pill pinned
 * to the bottom of the viewport, count → divider → Clear → actions), so every
 * listing behaves identically once rows are ticked.
 *
 * Purely PRESENTATIONAL, like both of those: every handler, modal and write
 * stays in ElectronicModules, which owns the selection.
 *
 * There is no bulk delete, matching the calibre listing — delete is a row-level
 * action in the editor. A module is the target of `WatchReference.moduleId` and
 * of a Cloud Function that mirrors it into each ref's `electronics` cache, so
 * deleting a set of them at once strands whatever still points at them with no
 * review step in between.
 */

export interface ModuleBulkActionBarProps {
  /** Number of selected modules. The caller renders the bar only when > 0. */
  count: number;
  /** A bulk write is in flight — disables every action. */
  busy?: boolean;
  onAiEnhance: () => void;
  onSetManufacturer: () => void;
  onSetSource: () => void;
  onSetImageSource: () => void;
  onClearSelection: () => void;
}

export function ModuleBulkActionBar({
  count,
  busy = false,
  onAiEnhance,
  onSetManufacturer,
  onSetSource,
  onSetImageSource,
  onClearSelection,
}: ModuleBulkActionBarProps) {
  const disabled = busy || count === 0;

  return (
    <div className='fixed bottom-6 left-1/2 -translate-x-1/2 z-50'>
      <div className='flex items-center gap-3 bg-gray-900 text-white px-5 py-3 rounded-2xl shadow-2xl'>
        <span className='text-sm font-medium whitespace-nowrap'>
          {count.toLocaleString()} selected
        </span>
        <div className='h-4 w-px bg-gray-600' />
        <button
          onClick={onClearSelection}
          disabled={busy}
          className='text-gray-400 hover:text-white disabled:opacity-50 text-sm transition-colors'
        >
          Clear
        </button>
        <button
          onClick={onAiEnhance}
          disabled={disabled}
          title='Run AI enrichment over the selected modules, then review and apply per-field'
          className='bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-sm px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors'
        >
          AI Enhance
        </button>
        <button
          onClick={onSetManufacturer}
          disabled={disabled}
          title='Set the manufacturer on every selected module that does not already have one'
          className='bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-sm px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors'
        >
          Set Manufacturer
        </button>
        <button
          onClick={onSetSource}
          disabled={disabled}
          title='Set the source on every selected module that does not already have one'
          className='bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-sm px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors'
        >
          Set source
        </button>
        <button
          onClick={onSetImageSource}
          disabled={disabled}
          title='Set the attribution source on every image of every selected module'
          className='bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-sm px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors'
        >
          Set image source
        </button>
      </div>
    </div>
  );
}
